/**
 * Card + its two optional strips, straight from the §13 mockup.
 *
 *   <Card>
 *     <CardHeader title="WordPress sites" chip="6 live"
 *                 description="…" actions={<Button …/>} />
 *     <CardTools>…tabs / search / secondary action…</CardTools>
 *     …body or table…
 *   </Card>
 *
 * Card clips its children (`overflow-hidden`) so a table's first/last rows sit
 * flush inside the 8px radius without each page re-deriving corner rules.
 */

export function Card({ children, className = '', ...rest }) {
  return (
    <div
      className={[
        'bg-white border border-bd rounded-card overflow-hidden',
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * Title + optional chip + optional description on the left, actions on the
 * right. `actions` is a node so a page keeps control of order and variants —
 * the primary button goes last, as in the mockup.
 */
export function CardHeader({ title, chip, description, actions, className = '' }) {
  return (
    <div
      className={[
        'flex items-start gap-3 px-5 py-[17px] border-b border-bd',
        className,
      ].join(' ')}
    >
      <div className="flex-1 min-w-0">
        <h2 className="flex items-center gap-[9px] text-base font-semibold text-t1">
          {title}
          {chip != null && <Chip>{chip}</Chip>}
        </h2>
        {description && (
          <p className="mt-1 text-[12.5px] text-t2">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

/**
 * The strip under a CardHeader: tabs, search, and a secondary action. Kept as a
 * plain layout slot — what goes inside differs per page, and inventing a Tabs
 * API before a second page needs it would be guesswork.
 */
export function CardTools({ children, className = '' }) {
  return (
    <div
      className={[
        'flex items-center gap-2.5 px-5 py-[13px] border-b border-bd bg-s-1',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

/** Small count/label badge used in a CardHeader title. */
export function Chip({ children, className = '' }) {
  return (
    <span
      className={[
        'inline-flex items-center px-2 py-[3px] rounded-ctl',
        'text-[11px] font-medium bg-s-blue text-blue border border-bd-blue',
        className,
      ].join(' ')}
    >
      {children}
    </span>
  );
}
